import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { fmt } from '../lib/helpers';
import { useSupabaseAuth } from '../context/AuthContext';
import useWorkspaceAccess from '../hooks/useWorkspaceAccess';
import Loader from '../components/Loader';
import { draftStatusReport, AiUnavailableError } from '../lib/ai';

const RAG_COLORS = { red: '#E24B4A', amber: '#EF9F27', green: '#1D9E75' };
const AREAS = [['schedule', 'Schedule'], ['budget', 'Budget'], ['resources', 'Resources'], ['risks', 'Risks']];
const RAG_RANK = { green: 0, amber: 1, red: 2 };
const worst = (vals) => vals.filter(Boolean).sort((a, b) => RAG_RANK[b] - RAG_RANK[a])[0] || 'green';
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');
const fromLines = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);

export default function StatusReports() {
  const { programme, projects } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [report, setReport] = useState(null);
  const [sections, setSections] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const [yearFilter, setYearFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [present, setPresent] = useState(false);

  const projName = useCallback((id) => projects.find((p) => p.id === id)?.name || 'Project', [projects]);

  const loadReports = useCallback(async () => {
    if (!programme?.id) return;
    setLoading(true);
    const { data } = await sb.from('status_reports').select('*').eq('programme_id', programme.id).order('week_ending', { ascending: false });
    setReports(data || []);
    setLoading(false);
    if (data?.length && !selectedId) setSelectedId(data[0].id);
  }, [programme?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadReports(); }, [loadReports]);

  // Load selected report + sections into editable state
  useEffect(() => {
    if (!selectedId) { setReport(null); setSections([]); return; }
    let cancelled = false;
    (async () => {
      const [rRes, sRes] = await Promise.all([
        sb.from('status_reports').select('*').eq('id', selectedId).maybeSingle(),
        sb.from('status_report_projects').select('*').eq('report_id', selectedId).order('order_index'),
      ]);
      if (cancelled) return;
      setReport(rRes.data || null);
      setSections(sRes.data || []);
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const years = useMemo(() => [...new Set(reports.map((r) => (r.week_ending || '').slice(0, 4)).filter(Boolean))], [reports]);
  const histReports = yearFilter === 'all' ? reports : reports.filter((r) => (r.week_ending || '').startsWith(yearFilter));
  const readonly = report?.status === 'published';

  async function createReport(prefill) {
    const we = ymd(addDays(new Date(), 7 - new Date().getDay() + 5)); // next Friday-ish
    const { data: rRows, error } = await sb.from('status_reports').insert({
      programme_id: programme.id, week_ending: prefill?.week_ending || we, author_id: profile?.id || null,
      author_name: profile?.full_name || null, status: 'draft',
      overall_rag: prefill?.overall_rag || null, section_rags: prefill?.section_rags || {},
      executive_summary: prefill?.executive_summary || null, highlights: prefill?.highlights || [],
      lowlights: prefill?.lowlights || [], look_ahead: prefill?.look_ahead || null, created_by: profile?.id || null,
    }).select('id');
    if (error) { setNotice(error.message); return; }
    const reportId = rRows[0].id;
    if (projects.length) {
      await sb.from('status_report_projects').insert(projects.map((p, i) => ({
        report_id: reportId, project_id: p.id, section_rags: {}, order_index: i + 1,
      })));
    }
    await loadReports();
    setSelectedId(reportId);
  }

  function patchReport(patch) { setReport((r) => ({ ...r, ...patch })); }
  function patchSection(id, patch) { setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s))); }

  async function save() {
    if (!report) return;
    setSaving(true); setNotice(null);
    const { error } = await sb.from('status_reports').update({
      week_ending: report.week_ending, author_name: report.author_name, overall_rag: report.overall_rag || null,
      section_rags: report.section_rags || {}, executive_summary: report.executive_summary || null,
      highlights: report.highlights || [], lowlights: report.lowlights || [], look_ahead: report.look_ahead || null,
    }).eq('id', report.id);
    for (const s of sections) {
      await sb.from('status_report_projects').update({
        section_rags: s.section_rags || {}, summary: s.summary || null, highlights: s.highlights || [],
        lowlights: s.lowlights || [], stats: s.stats || {},
      }).eq('id', s.id);
    }
    setSaving(false);
    if (error) setNotice(error.message); else setNotice('Saved.');
    await loadReports();
  }

  async function publish() {
    await save();
    await sb.from('status_reports').update({ status: 'published', published_at: new Date().toISOString() }).eq('id', report.id);
    setReport((r) => ({ ...r, status: 'published', published_at: new Date().toISOString() }));
    await loadReports();
  }

  async function duplicateAsDraft() {
    await createReport({
      week_ending: ymd(addDays(new Date(report.week_ending), 7)), overall_rag: report.overall_rag,
      section_rags: report.section_rags, executive_summary: report.executive_summary,
      highlights: report.highlights, lowlights: report.lowlights, look_ahead: report.look_ahead,
    });
  }

  async function generateDraft() {
    setDrafting(true); setNotice(null);
    try {
      const live = await gatherLiveData(programme.id, projects);
      let draft;
      try {
        draft = await draftStatusReport({ programmeName: programme.name, weekEnding: report.week_ending, data: live });
      } catch (e) {
        if (e instanceof AiUnavailableError) { draft = computeLocalDraft(projects, live); setNotice('AI service not deployed — generated a draft from live data instead.'); }
        else throw e;
      }
      // apply to report
      patchReport({
        overall_rag: draft.overall_rag || null, section_rags: draft.section_rags || {},
        executive_summary: draft.executive_summary || '', highlights: draft.highlights || [],
        lowlights: draft.lowlights || [], look_ahead: draft.look_ahead || '',
      });
      // apply to sections by project_id
      const byProj = {}; (draft.projects || []).forEach((p) => { byProj[p.project_id] = p; });
      setSections((prev) => prev.map((s) => {
        const d = byProj[s.project_id] || {};
        const stat = (live.projectStats || {})[s.project_id] || {};
        return { ...s, section_rags: d.section_rags || s.section_rags, summary: d.summary ?? s.summary, highlights: d.highlights || s.highlights, lowlights: d.lowlights || s.lowlights, stats: stat };
      }));
    } catch (e) {
      setNotice(e?.message || 'Could not generate draft.');
    } finally {
      setDrafting(false);
    }
  }

  if (loading) return <div className="content"><Loader text="Loading status reports…" /></div>;

  return (
    <div className="content">
      <div className="sr-layout">
        {showHistory && (
          <div className="sr-history">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span className="ctitle">History</span>
              <select className="form-select" style={{ width: 'auto', fontSize: 11 }} value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                <option value="all">All</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {canWrite && <button className="btn-primary" style={{ width: '100%', marginBottom: 10 }} onClick={() => createReport()}>+ New report</button>}
            {histReports.length === 0 ? <div style={{ fontSize: 11, color: '#888780' }}>No reports yet.</div> : histReports.map((r) => (
              <div key={r.id} className={`sr-hist-item${selectedId === r.id ? ' active' : ''}`} onClick={() => setSelectedId(r.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {r.overall_rag && <span className="sr-rag-circle" style={{ background: RAG_COLORS[r.overall_rag] }} />}
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: '#2c2c2a' }}>{fmt(r.week_ending)}</span>
                </div>
                <div style={{ fontSize: 10.5, color: '#888780', marginTop: 2 }}>{r.author_name || '—'} · <span className={`b ${r.status === 'published' ? 'gr' : ''}`} style={{ fontSize: 9 }}>{r.status}</span></div>
              </div>
            ))}
          </div>
        )}

        <div className="sr-content">
          {!report ? (
            <div className="empty" style={{ marginTop: 40 }}>Select a report from history, or create a new one.</div>
          ) : (
            <ReportEditor
              report={report} sections={sections} readonly={readonly || !canWrite} projName={projName}
              showHistory={showHistory} setShowHistory={setShowHistory}
              patchReport={patchReport} patchSection={patchSection}
              onSave={save} onPublish={publish} onDuplicate={duplicateAsDraft} onGenerate={generateDraft}
              onPresent={() => setPresent(true)} saving={saving} drafting={drafting} notice={notice}
              programme={programme} canWrite={canWrite}
            />
          )}
        </div>
      </div>

      {present && report && (
        <Presentation report={report} sections={sections} programme={programme} projName={projName} onClose={() => setPresent(false)} />
      )}
    </div>
  );
}

// ── RAG picker ─────────────────────────────────────────────────────────────────
function RagPicker({ value, onChange, disabled }) {
  return (
    <span className="sr-rag-pick">
      {['red', 'amber', 'green'].map((v) => (
        <button key={v} type="button" className={`sr-rag-btn ${v}${value === v ? ' sel' : ''}`} disabled={disabled} onClick={() => onChange(v)} title={v} />
      ))}
    </span>
  );
}

function EditableArea({ label, value, onChange, readonly, rows = 3, placeholder }) {
  return (
    <div className="sr-section">
      <div className="sr-section-h">{label}</div>
      {readonly ? <div style={{ fontSize: 12.5, color: '#2c2c2a', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{value || '—'}</div>
        : <textarea className="form-textarea" rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
}

function BulletArea({ label, items, onChange, readonly, tone }) {
  return (
    <div className="sr-section">
      <div className="sr-section-h">{label}</div>
      {readonly ? (
        (items || []).length === 0 ? <div style={{ fontSize: 12, color: '#888780' }}>—</div> :
        <ul className={`sr-bullets ${tone}`}>{items.map((b, i) => <li key={i}>{b}</li>)}</ul>
      ) : (
        <textarea className="form-textarea" rows={4} value={toLines(items)} onChange={(e) => onChange(fromLines(e.target.value))} placeholder="One item per line" />
      )}
    </div>
  );
}

// ── Report editor ────────────────────────────────────────────────────────────
function ReportEditor({ report, sections, readonly, projName, showHistory, setShowHistory, patchReport, patchSection, onSave, onPublish, onDuplicate, onGenerate, onPresent, saving, drafting, notice, programme, canWrite }) {
  const setSecRag = (s, area, v) => patchSection(s.id, { section_rags: { ...(s.section_rags || {}), [area]: v } });
  return (
    <div>
      {/* toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <button className="btn-ghost" onClick={() => setShowHistory(!showHistory)}>{showHistory ? '⟨ Hide history' : '⟩ Show history'}</button>
        <div style={{ flex: 1 }} />
        {!readonly && <button className="btn-ghost" onClick={onGenerate} disabled={drafting}>{drafting ? 'Generating…' : '✦ Generate draft'}</button>}
        <button className="btn-ghost" onClick={() => copyReport(report, sections, projName)}>Copy</button>
        <button className="btn-ghost" onClick={() => exportReport(report, sections, projName, programme)}>Export</button>
        <button className="btn-ghost" onClick={onPresent}>▶ Present</button>
        {!readonly && <button className="btn-ghost" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
        {canWrite && (readonly
          ? <button className="btn-primary" onClick={onDuplicate}>Duplicate as draft</button>
          : <button className="btn-primary" onClick={onPublish}>Publish report</button>)}
      </div>
      {notice && <div className="gate-banner info" style={{ marginBottom: 12 }}>{notice}</div>}

      {/* header */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#2c2c2a' }}>{programme.name} — weekly status</div>
            <div style={{ fontSize: 12, color: '#888780', marginTop: 3, display: 'flex', gap: 14, alignItems: 'center' }}>
              <span>Week ending {readonly ? fmt(report.week_ending) : <input type="date" className="form-input" style={{ display: 'inline-block', width: 'auto', padding: '2px 6px' }} value={report.week_ending || ''} onChange={(e) => patchReport({ week_ending: e.target.value })} />}</span>
              <span>{report.author_name || '—'}</span>
              <span className={`b ${report.status === 'published' ? 'gr' : ''}`}>{report.status}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: '#888780' }}>Overall</span>
            {readonly ? <span className="sr-rag-circle" style={{ width: 20, height: 20, background: RAG_COLORS[report.overall_rag] || '#d3d1c7' }} />
              : <RagPicker value={report.overall_rag} onChange={(v) => patchReport({ overall_rag: v })} />}
          </div>
        </div>

        {/* status by area */}
        <div className="sr-area-grid" style={{ marginTop: 16 }}>
          {AREAS.map(([key, label]) => (
            <div key={key} className="sr-area">
              <span className="sr-area-lbl">{label}</span>
              {readonly ? <span className="sr-rag-circle" style={{ background: RAG_COLORS[report.section_rags?.[key]] || '#d3d1c7' }} />
                : <RagPicker value={report.section_rags?.[key]} onChange={(v) => patchReport({ section_rags: { ...(report.section_rags || {}), [key]: v } })} />}
            </div>
          ))}
        </div>

        <EditableArea label="Executive summary" value={report.executive_summary} onChange={(v) => patchReport({ executive_summary: v })} readonly={readonly} rows={4} />
        <BulletArea label="Highlights" items={report.highlights} onChange={(v) => patchReport({ highlights: v })} readonly={readonly} tone="g" />
        <BulletArea label="Issues & concerns" items={report.lowlights} onChange={(v) => patchReport({ lowlights: v })} readonly={readonly} tone="r" />
        <EditableArea label="Look ahead (next 2 weeks)" value={report.look_ahead} onChange={(v) => patchReport({ look_ahead: v })} readonly={readonly} rows={3} />
      </div>

      {/* project sections */}
      {sections.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="sr-section-h" style={{ marginBottom: 10 }}>Project sections</div>
          {sections.map((s) => (
            <div key={s.id} className="sr-proj-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#2c2c2a' }}>{projName(s.project_id)}</span>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  {AREAS.map(([key, label]) => (
                    <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: '#888780' }}>
                      {label[0]}
                      {readonly ? <span className="sr-rag-circle" style={{ background: RAG_COLORS[s.section_rags?.[key]] || '#d3d1c7' }} /> : <RagPicker value={s.section_rags?.[key]} onChange={(v) => setSecRag(s, key, v)} />}
                    </span>
                  ))}
                </div>
              </div>
              {s.stats && Object.keys(s.stats).length > 0 && (
                <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#5f5e5a', marginBottom: 8 }}>
                  {s.stats.gate_pct != null && <span>Gate readiness: <strong>{s.stats.gate_pct}%</strong></span>}
                  {s.stats.open_raid != null && <span>Open RAID: <strong>{s.stats.open_raid}</strong></span>}
                  {s.stats.resources != null && <span>Resources: <strong>{s.stats.resources}</strong></span>}
                </div>
              )}
              <EditableArea label="Summary" value={s.summary} onChange={(v) => patchSection(s.id, { summary: v })} readonly={readonly} rows={2} />
              <BulletArea label="Highlights" items={s.highlights} onChange={(v) => patchSection(s.id, { highlights: v })} readonly={readonly} tone="g" />
              <BulletArea label="Concerns" items={s.lowlights} onChange={(v) => patchSection(s.id, { lowlights: v })} readonly={readonly} tone="r" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Presentation mode ──────────────────────────────────────────────────────────
function Presentation({ report, sections, programme, projName, onClose }) {
  const slides = useMemo(() => {
    const s = [
      { kind: 'title' }, { kind: 'health' }, { kind: 'areas' }, { kind: 'bullets' }, { kind: 'look' },
      ...sections.map((sec) => ({ kind: 'project', sec })),
    ];
    return s;
  }, [sections]);
  const [i, setI] = useState(0);
  const go = useCallback((n) => setI((cur) => Math.max(0, Math.min(slides.length - 1, cur + n))), [slides.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  const sl = slides[i];
  return (
    <div className="pres-overlay">
      <button className="pres-close" onClick={onClose}>✕</button>
      <div className="pres-slide">
        {sl.kind === 'title' && (<><h1>{programme.name}</h1><div style={{ fontSize: 20, color: '#5f5e5a', marginTop: 12 }}>Weekly status — week ending {fmt(report.week_ending)}</div><div style={{ fontSize: 15, color: '#888780', marginTop: 8 }}>{report.author_name}</div></>)}
        {sl.kind === 'health' && (<><div className="pres-rag-big" style={{ background: RAG_COLORS[report.overall_rag] || '#d3d1c7' }} /><h2>Overall health</h2><div style={{ fontSize: 17, color: '#2c2c2a', maxWidth: 720, lineHeight: 1.6 }}>{report.executive_summary || '—'}</div></>)}
        {sl.kind === 'areas' && (<><h2>Status by area</h2><div className="pres-area-grid">{AREAS.map(([k, l]) => <div key={k} className="pres-area"><span>{l}</span><span className="sr-rag-circle" style={{ width: 26, height: 26, background: RAG_COLORS[report.section_rags?.[k]] || '#d3d1c7' }} /></div>)}</div></>)}
        {sl.kind === 'bullets' && (<><h2>Highlights &amp; concerns</h2><div className="pres-cols"><div><div className="sr-section-h" style={{ color: '#1D9E75' }}>Highlights</div><ul className="sr-bullets g">{(report.highlights || []).map((b, x) => <li key={x} style={{ fontSize: 16 }}>{b}</li>)}</ul></div><div><div className="sr-section-h" style={{ color: '#A32D2D' }}>Concerns</div><ul className="sr-bullets r">{(report.lowlights || []).map((b, x) => <li key={x} style={{ fontSize: 16 }}>{b}</li>)}</ul></div></div></>)}
        {sl.kind === 'look' && (<><h2>Look ahead</h2><div style={{ fontSize: 18, color: '#2c2c2a', maxWidth: 760, lineHeight: 1.6 }}>{report.look_ahead || '—'}</div></>)}
        {sl.kind === 'project' && (<><h2>{projName(sl.sec.project_id)}</h2><div className="pres-area-grid" style={{ marginBottom: 20 }}>{AREAS.map(([k, l]) => <div key={k} className="pres-area"><span>{l}</span><span className="sr-rag-circle" style={{ width: 24, height: 24, background: RAG_COLORS[sl.sec.section_rags?.[k]] || '#d3d1c7' }} /></div>)}</div><div style={{ fontSize: 16, color: '#2c2c2a', maxWidth: 720, lineHeight: 1.6 }}>{sl.sec.summary || '—'}</div></>)}
      </div>
      <div className="pres-nav">
        <button className="btn-ghost" onClick={() => go(-1)} disabled={i === 0}>←</button>
        {slides.map((_, x) => <button key={x} className={`pres-dot${x === i ? ' on' : ''}`} onClick={() => setI(x)} />)}
        <button className="btn-ghost" onClick={() => go(1)} disabled={i === slides.length - 1}>→</button>
      </div>
    </div>
  );
}

// ── Live data + local draft ────────────────────────────────────────────────────
async function gatherLiveData(programmeId, projects) {
  const projectIds = projects.map((p) => p.id);
  const [{ data: raid }, { data: gates }] = await Promise.all([
    sb.from('raid_items').select('id, type, status, priority, title, project_id').eq('programme_id', programmeId),
    projectIds.length ? sb.from('stage_gates').select('id, project_id, status').in('project_id', projectIds) : Promise.resolve({ data: [] }),
  ]);
  const gateIds = (gates || []).map((g) => g.id);
  const { data: criteria } = gateIds.length
    ? await sb.from('gate_criteria').select('gate_id, is_met').in('gate_id', gateIds)
    : { data: [] };
  const { data: allocs } = projectIds.length
    ? await sb.from('resource_allocations').select('profile_id, project_id').in('project_id', projectIds)
    : { data: [] };

  const gateByProject = {}; (gates || []).forEach((g) => { (gateByProject[g.project_id] ||= []).push(g.id); });
  const projectStats = {};
  const projSummaries = projects.map((p) => {
    const pgIds = gateByProject[p.id] || [];
    const crit = (criteria || []).filter((c) => pgIds.includes(c.gate_id));
    const met = crit.filter((c) => c.is_met).length;
    const pct = crit.length ? Math.round((met / crit.length) * 100) : 0;
    const openRaid = (raid || []).filter((r) => r.project_id === p.id && r.status !== 'closed').length;
    const critical = (raid || []).filter((r) => r.project_id === p.id && r.status !== 'closed' && r.priority === 'critical').length;
    const resCount = new Set((allocs || []).filter((a) => a.project_id === p.id).map((a) => a.profile_id)).size;
    projectStats[p.id] = { gate_pct: pct, open_raid: openRaid, resources: resCount };
    return { project_id: p.id, name: p.name, gate_pct: pct, open_raid: openRaid, critical, resources: resCount };
  });

  return {
    raidOpen: (raid || []).filter((r) => r.status !== 'closed').length,
    raidCritical: (raid || []).filter((r) => r.status !== 'closed' && r.priority === 'critical').length,
    projects: projSummaries, projectStats,
  };
}

function ragFor({ gate_pct, open_raid, critical }) {
  const schedule = gate_pct < 50 ? 'red' : gate_pct < 80 ? 'amber' : 'green';
  const risks = critical > 0 ? 'red' : open_raid > 3 ? 'amber' : 'green';
  return { schedule, budget: 'green', resources: 'green', risks };
}

function computeLocalDraft(projects, live) {
  const projDrafts = (live.projects || []).map((p) => {
    const rags = ragFor(p);
    return {
      project_id: p.project_id, section_rags: rags,
      summary: `${p.name}: ${p.gate_pct}% of gate criteria met; ${p.open_raid} open RAID item${p.open_raid === 1 ? '' : 's'}${p.critical ? `, ${p.critical} critical` : ''}.`,
      highlights: p.gate_pct >= 80 ? ['Gate readiness on track'] : [],
      lowlights: p.critical ? [`${p.critical} critical RAID item(s) open`] : p.gate_pct < 50 ? ['Gate readiness behind plan'] : [],
    };
  });
  const overall = worst(projDrafts.map((p) => worst(Object.values(p.section_rags))));
  const section_rags = {
    schedule: worst(projDrafts.map((p) => p.section_rags.schedule)),
    budget: 'green',
    resources: 'green',
    risks: worst(projDrafts.map((p) => p.section_rags.risks)),
  };
  return {
    overall_rag: overall, section_rags,
    executive_summary: `Across ${projects.length} project${projects.length === 1 ? '' : 's'}, ${live.raidOpen} RAID item${live.raidOpen === 1 ? '' : 's'} remain open${live.raidCritical ? ` (${live.raidCritical} critical)` : ''}. Overall status is ${overall}.`,
    highlights: projDrafts.flatMap((p) => p.highlights),
    lowlights: projDrafts.flatMap((p) => p.lowlights),
    look_ahead: 'Focus the next two weeks on closing critical RAID items and advancing gate readiness toward the next formal review.',
    projects: projDrafts,
  };
}

// ── Export helpers ───────────────────────────────────────────────────────────
function reportText(report, sections, projName) {
  const lines = [];
  lines.push(`WEEKLY STATUS — week ending ${report.week_ending}`);
  lines.push(`Author: ${report.author_name || ''}  |  Overall: ${report.overall_rag || '—'}`);
  lines.push('');
  AREAS.forEach(([k, l]) => lines.push(`${l}: ${report.section_rags?.[k] || '—'}`));
  lines.push('', 'EXECUTIVE SUMMARY', report.executive_summary || '—');
  lines.push('', 'HIGHLIGHTS', ...(report.highlights || []).map((b) => `• ${b}`));
  lines.push('', 'ISSUES & CONCERNS', ...(report.lowlights || []).map((b) => `• ${b}`));
  lines.push('', 'LOOK AHEAD', report.look_ahead || '—');
  sections.forEach((s) => {
    lines.push('', `— ${projName(s.project_id)} —`, s.summary || '');
    (s.highlights || []).forEach((b) => lines.push(`  + ${b}`));
    (s.lowlights || []).forEach((b) => lines.push(`  - ${b}`));
  });
  return lines.join('\n');
}

function copyReport(report, sections, projName) {
  navigator.clipboard?.writeText(reportText(report, sections, projName));
}

function exportReport(report, sections, projName, programme) {
  const html = `<html><head><meta charset="utf-8"><title>Status report</title></head><body style="font-family:Arial,sans-serif;color:#2c2c2a;padding:24px;white-space:pre-wrap;">`
    + `<h1>${programme.name} — weekly status</h1>`
    + `<pre style="font-family:inherit;white-space:pre-wrap;">${reportText(report, sections, projName).replace(/</g, '&lt;')}</pre>`
    + `</body></html>`;
  const blob = new Blob([html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `status-${report.week_ending}.doc`; a.click();
  URL.revokeObjectURL(url);
}
